// -----------------------------------------------------------------------------
// relativeDates.ts
//
// Relative ("dynamic") date values for the report builder's WHERE clause.
//
// A saved report that filters on a typed-in date goes stale the moment it is
// saved: "Due Date is 2026-08-31" means nothing next week. These presets let a
// condition say *today*, *the last 30 days*, *last month* instead, and are
// resolved to concrete calendar days at Execute time — so re-running (or
// re-opening) a saved report always re-reads the current date.
//
// Two rules shape everything here:
//
//  1. **Local calendar days, DST-proof.** Every bound is a `YYYY-MM-DD` local
//     calendar day, exactly like `utility/dateFilter` — `toISOString()` would
//     hand a user west of UTC *tomorrow* all evening. The arithmetic itself runs
//     through `Date.UTC` (a fixed 24h calendar with automatic month/year
//     rollover), so "last 30 days" can't gain or lose a day across a DST switch.
//
//  2. **A preset is always a half-open range** `[start, endExclusive)`, never a
//     single value. A `datetime` column holds a time, so `= '2026-08-31'` only
//     matches rows stamped exactly midnight; "is today" has to be
//     `>= today AND < tomorrow`. {@link dateBoundsFor} turns the user's operator
//     plus that range into the one or two comparisons the BFF needs.
//
// The resolution is pure and unit-tested (`relativeDates.test.ts`); `now` is a
// parameter, never read from the clock here, so the tests are deterministic.
// -----------------------------------------------------------------------------

import type { OperandType } from "./reportQuery";
import type { OperatorOption } from "./reportQuery";

/**
 * Which date a condition's value comes from. "Specific" is the original
 * behaviour — the absolute date the user typed — and is the default for every
 * condition, including every report saved before presets existed.
 */
export type DatePresetId =
  | "Specific"
  | "Today"
  | "Yesterday"
  | "Tomorrow"
  | "LastNDays"
  | "NextNDays"
  | "ThisWeek"
  | "LastWeek"
  | "ThisMonth"
  | "LastMonth"
  | "ThisQuarter"
  | "LastQuarter"
  | "ThisYear"
  | "LastYear"
  | "YearToDate";

/** A resolved preset: local calendar days, `start` inclusive, `endExclusive` not. */
export interface DateRange {
  start: string;
  endExclusive: string;
}

/** Days to preselect for the two "last/next N days" presets. */
export const DEFAULT_RELATIVE_DAYS = 7;

/** Ceiling on N for the rolling presets (~10 years) — enough for any report,
 *  and it keeps a fat-fingered "77777" from producing a nonsense year. */
export const MAX_RELATIVE_DAYS = 3650;

export interface DatePresetOption {
  value: DatePresetId;
  text: string;
  /** True when the preset needs a day count from the user (`dateOffset`). */
  needsCount?: boolean;
}

/**
 * The preset list, in the order it appears in the dropdown: the typed-in date
 * first (so the default reads as the default), then the single days, the two
 * rolling windows, then the calendar periods from shortest to longest.
 */
export const DATE_PRESETS: DatePresetOption[] = [
  { value: "Specific", text: "Specific date" },
  { value: "Today", text: "Today" },
  { value: "Yesterday", text: "Yesterday" },
  { value: "Tomorrow", text: "Tomorrow" },
  { value: "LastNDays", text: "Last N days", needsCount: true },
  { value: "NextNDays", text: "Next N days", needsCount: true },
  { value: "ThisWeek", text: "This week" },
  { value: "LastWeek", text: "Last week" },
  { value: "ThisMonth", text: "This month" },
  { value: "LastMonth", text: "Last month" },
  { value: "ThisQuarter", text: "This quarter" },
  { value: "LastQuarter", text: "Last quarter" },
  { value: "YearToDate", text: "Year to date" },
  { value: "ThisYear", text: "This year" },
  { value: "LastYear", text: "Last year" },
];

/** A stored/typed value narrowed to a known preset; anything else ⇒ "Specific". */
export function normalizeDatePreset(value: unknown): DatePresetId {
  return DATE_PRESETS.some((p) => p.value === value) ? (value as DatePresetId) : "Specific";
}

/** Is this a relative preset (i.e. resolved from the clock rather than typed)? */
export const isRelativePreset = (preset: DatePresetId | undefined): boolean =>
  Boolean(preset) && preset !== "Specific";

/** Does the preset take a day count ("Last N days" / "Next N days")? */
export const presetNeedsCount = (preset: DatePresetId | undefined): boolean =>
  Boolean(DATE_PRESETS.find((p) => p.value === preset)?.needsCount);

const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * A local calendar day as `YYYY-MM-DD`, built through `Date.UTC` so out-of-range
 * parts roll over (month 12 ⇒ next January, day 0 ⇒ last day of the previous
 * month) without a local offset or a DST transition ever entering the result.
 * `month` is 0-based, like `Date`.
 */
const dayKey = (year: number, month: number, day: number): string => {
  const t = new Date(Date.UTC(year, month, day));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
};

/** N for a rolling preset: a whole number of days, clamped to 1…MAX. Returns
 *  null for a blank/unparseable count so the caller can drop the condition
 *  rather than silently filter on a made-up window. */
const dayCount = (offset: number | undefined): number | null => {
  const n = Math.floor(Number(offset));
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.min(n, MAX_RELATIVE_DAYS);
};

/**
 * Resolve a preset against `now` (the local clock) to a half-open range of
 * local calendar days. Returns null for "Specific"/unknown presets and for a
 * rolling preset with no usable day count.
 *
 * Conventions worth stating because they're choices, not facts:
 *  - **"Last N days" includes today** (N = 7 ⇒ today and the six days before
 *    it), and **"Next N days" also includes today** — that's what ServiceNow and
 *    Salesforce do, and what an operator asking for "the last 7 days" means.
 *  - **Weeks start Sunday**, matching the shop floor's "This Week" due-date
 *    filter (`utility/dateFilter.thisWeekRange`) so the two agree. This is
 *    deliberately *not* the ISO (Monday) week the Capacity Manager groups by.
 *  - **"Year to date" ends today**, not at the end of the year.
 */
export function resolveDatePreset(
  preset: DatePresetId | undefined,
  offset: number | undefined,
  now: Date = new Date()
): DateRange | null {
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const range = (start: string, endExclusive: string): DateRange => ({ start, endExclusive });

  switch (preset) {
    case "Today":
      return range(dayKey(y, m, d), dayKey(y, m, d + 1));
    case "Yesterday":
      return range(dayKey(y, m, d - 1), dayKey(y, m, d));
    case "Tomorrow":
      return range(dayKey(y, m, d + 1), dayKey(y, m, d + 2));
    case "LastNDays": {
      const n = dayCount(offset);
      return n === null ? null : range(dayKey(y, m, d - (n - 1)), dayKey(y, m, d + 1));
    }
    case "NextNDays": {
      const n = dayCount(offset);
      return n === null ? null : range(dayKey(y, m, d), dayKey(y, m, d + n));
    }
    case "ThisWeek": {
      const sunday = d - now.getDay();
      return range(dayKey(y, m, sunday), dayKey(y, m, sunday + 7));
    }
    case "LastWeek": {
      const sunday = d - now.getDay() - 7;
      return range(dayKey(y, m, sunday), dayKey(y, m, sunday + 7));
    }
    case "ThisMonth":
      return range(dayKey(y, m, 1), dayKey(y, m + 1, 1));
    case "LastMonth":
      return range(dayKey(y, m - 1, 1), dayKey(y, m, 1));
    case "ThisQuarter": {
      const q = Math.floor(m / 3) * 3;
      return range(dayKey(y, q, 1), dayKey(y, q + 3, 1));
    }
    case "LastQuarter": {
      const q = Math.floor(m / 3) * 3 - 3;
      return range(dayKey(y, q, 1), dayKey(y, q + 3, 1));
    }
    case "ThisYear":
      return range(dayKey(y, 0, 1), dayKey(y + 1, 0, 1));
    case "LastYear":
      return range(dayKey(y - 1, 0, 1), dayKey(y, 0, 1));
    case "YearToDate":
      return range(dayKey(y, 0, 1), dayKey(y, m, d + 1));
    default:
      return null;
  }
}

/** The day before an exclusive upper bound — the last day actually matched,
 *  which is what a person needs to see. */
export function inclusiveEnd(endExclusive: string): string {
  const [y, m, d] = endExclusive.split("-").map(Number);
  return dayKey(y, m - 1, d - 1);
}

/** Plain-language "which dates will this match", shown under the condition so
 *  nobody has to guess what "Last quarter" resolved to. */
export function describeDateRange(
  range: DateRange,
  format: (day: string) => string = (day) => day
): string {
  const end = inclusiveEnd(range.endExclusive);
  return range.start === end ? format(range.start) : `${format(range.start)} → ${format(end)}`;
}

// ---- Operators against a range ----------------------------------------------

/**
 * The operators offered once a condition uses a relative preset, relabelled for
 * a *range* ("is within" rather than "is").
 *
 * "is not" is deliberately absent. Excluding a range means
 * `field < start OR field >= end`, and the BFF's WHERE clause is a flat list
 * with no parentheses (see `ReportSqlBuilder.Build`), so that OR would bind to
 * the neighbouring conditions and quietly return the wrong rows. "is before" /
 * "is after" cover the same ground safely.
 */
const RELATIVE_OP_LABELS: { value: OperandType; label: string }[] = [
  { value: "Equals", label: "is within" },
  { value: "IsMoreThanOrEqualTo", label: "is on or after" },
  { value: "IsMoreThan", label: "is after" },
  { value: "IsLessThan", label: "is before" },
  { value: "IsLessThanOrEqualTo", label: "is on or before" },
];

export function relativeDateOperators(): OperatorOption[] {
  return RELATIVE_OP_LABELS.map((o) => ({ value: o.value, label: o.label }));
}

/** Is this operator meaningful against a relative range? */
export const isRelativeDateOperator = (operator: OperandType): boolean =>
  RELATIVE_OP_LABELS.some((o) => o.value === operator);

/** The operator to fall back on when a condition switches to a relative preset
 *  while holding one the range form can't express (i.e. "is not"). */
export const DEFAULT_RELATIVE_OPERATOR: OperandType = "Equals";

/**
 * What changes on a date condition when the user picks a preset. Two things
 * travel with the choice, and both are easy to forget in the component:
 *  - a rolling preset needs a day count, so it is seeded (with whatever count
 *    the row already had, else {@link DEFAULT_RELATIVE_DAYS}) and cleared again
 *    for a preset that doesn't take one;
 *  - a relative preset compares against a *period*, so an operator the range
 *    form can't express ("is not") falls back to "is within" — otherwise the row
 *    would look filtered while {@link dateBoundsFor} quietly drops it.
 */
export function applyDatePreset(
  datePreset: DatePresetId,
  current: { operator: OperandType; dateOffset?: number }
): { datePreset: DatePresetId; dateOffset?: number; operator: OperandType } {
  const needsRangeOperator = isRelativePreset(datePreset) && !isRelativeDateOperator(current.operator);
  return {
    datePreset,
    dateOffset: presetNeedsCount(datePreset)
      ? current.dateOffset ?? DEFAULT_RELATIVE_DAYS
      : undefined,
    operator: needsRangeOperator ? DEFAULT_RELATIVE_OPERATOR : current.operator,
  };
}

/** One comparison to emit for a relative condition. */
export interface DateBound {
  operator: OperandType;
  value: string;
}

/**
 * The comparison(s) a relative condition becomes. Every bound is ANDed by the
 * caller — never ORed — so the flat WHERE clause stays correct whatever the
 * neighbouring conditions do (AND binds tighter than OR in T-SQL, so an
 * `... OR f >= start AND f < end` still means what the row says).
 *
 *  - "is within"      ⇒ `>= start` AND `< endExclusive` (the whole period)
 *  - "is on or after" ⇒ `>= start`
 *  - "is after"       ⇒ `>= endExclusive` (strictly past the whole period)
 *  - "is before"      ⇒ `< start`
 *  - "is on or before"⇒ `< endExclusive` (through the end of the period)
 *
 * An operator the range form can't express yields `[]`, and the caller drops
 * the condition rather than guessing.
 */
export function dateBoundsFor(operator: OperandType, range: DateRange): DateBound[] {
  switch (operator) {
    case "Equals":
      return [
        { operator: "IsMoreThanOrEqualTo", value: range.start },
        { operator: "IsLessThan", value: range.endExclusive },
      ];
    case "IsMoreThanOrEqualTo":
      return [{ operator: "IsMoreThanOrEqualTo", value: range.start }];
    case "IsMoreThan":
      return [{ operator: "IsMoreThanOrEqualTo", value: range.endExclusive }];
    case "IsLessThan":
      return [{ operator: "IsLessThan", value: range.start }];
    case "IsLessThanOrEqualTo":
      return [{ operator: "IsLessThan", value: range.endExclusive }];
    default:
      return [];
  }
}
